import * as React from "react";
import {
	Alert,
	Autocomplete,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { useCampaignsList } from "@/hooks/api/use-campaigns";
import { IntakeKey, useCampaignReadiness, useCreateIntakeKey, useUpdateIntakeKey } from "@/hooks/api/use-intake-keys";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

interface FormValues {
	name: string;
	campaignId: string;
	allowedCountries: string[];
	allowedIps: string[];
	defaultCountry: string;
	/** One chip per mapping: "company", or "help_with:notes" to rename. */
	fieldMap: string[];
	rateLimitPerHour: string;
	cooldownSeconds: string;
	enabled: boolean;
}

/** The map the API applies when a key is created without one. */
const DEFAULT_FIELD_MAP = ["name", "email"];

/**
 * Chips carry a mapping each. A submitted key and its contact attribute are
 * usually the same word, so a bare chip means "map it onto itself" and the
 * "from:to" form is there for the rarer rename.
 */
function fieldMapToChips(fieldMap: Record<string, string> | undefined): string[] {
	return Object.entries(fieldMap ?? {}).map(([from, to]) => (from === to ? from : `${from}:${to}`));
}

function chipsToFieldMap(chips: string[]): Record<string, string> {
	return Object.fromEntries(
		chips
			.map((chip) => {
				const [from, to] = chip.split(":").map((part) => part.trim());
				return [from, to || from];
			})
			.filter(([from]) => from)
	);
}

function defaults(key: IntakeKey | null): FormValues {
	return {
		name: key?.name ?? "",
		campaignId: key?.campaignId ?? "",
		allowedCountries: key?.allowedCountries ?? [],
		allowedIps: key?.allowedIps ?? [],
		defaultCountry: key?.defaultCountry ?? "",
		fieldMap: key ? fieldMapToChips(key.fieldMap) : DEFAULT_FIELD_MAP,
		rateLimitPerHour: String(key?.rateLimitPerHour ?? 60),
		cooldownSeconds: String(key?.cooldownSeconds ?? 900),
		enabled: key?.enabled ?? true,
	};
}

export function CreateIntakeKey({
	intakeKey,
	isDrawerOpen,
	onDrawerClose,
	onSecretIssued,
}: {
	intakeKey: IntakeKey | null;
	isDrawerOpen: boolean;
	onDrawerClose: () => void;
	onSecretIssued: (secret: { secret: string; keyId: string }) => void;
}): React.JSX.Element {
	const createMutation = useCreateIntakeKey();
	const updateMutation = useUpdateIntakeKey();
	// `limit`/`offset` are NOT optional on the API's PaginatedQuery - they carry
	// @IsNumber/@IsInt with no @IsOptional, so omitting them makes GET /campaigns
	// fail validation with a 400 and the dropdown silently renders empty.
	const {
		data: campaigns,
		isError: campaignsFailed,
		isLoading: campaignsLoading,
	} = useCampaignsList("limit=100&offset=0");

	const isEdit = !!intakeKey?.id;

	const methods = useForm<FormValues>({ defaultValues: defaults(intakeKey) });
	const {
		handleSubmit,
		reset,
		control,
		watch,
		formState: { isSubmitting },
	} = methods;

	React.useLayoutEffect(() => {
		reset(defaults(intakeKey));
	}, [intakeKey, reset, isDrawerOpen]);

	const campaignId = watch("campaignId");
	const { data: readiness } = useCampaignReadiness(campaignId || null);

	const campaignOptions = React.useMemo(() => {
		const list = campaigns?.data ?? campaigns ?? [];
		return Array.isArray(list) ? list : [];
	}, [campaigns]);

	const onSubmitForm = async (data: FormValues) => {
		const payload: Record<string, unknown> = {
			name: data.name,
			campaignId: data.campaignId,
			allowedCountries: data.allowedCountries.map((c) => c.toUpperCase()),
			allowedIps: data.allowedIps,
			defaultCountry: data.defaultCountry ? data.defaultCountry.toUpperCase() : null,
			// Sent whole, not merged - the API replaces the stored map with this one.
			fieldMap: chipsToFieldMap(data.fieldMap),
			rateLimitPerHour: Number(data.rateLimitPerHour) || 60,
			// A cleared field must fall back to the default, not to 0. `|| 0` would
			// silently turn duplicate suppression off; an explicit 0 is still honoured.
			cooldownSeconds: data.cooldownSeconds === "" ? 900 : Number(data.cooldownSeconds),
		};

		try {
			if (isEdit && intakeKey?.id) {
				await updateMutation.mutateAsync({ id: intakeKey.id, data: { ...payload, enabled: data.enabled } });
			} else {
				const issued = await createMutation.mutateAsync({ data: payload });
				onSecretIssued({ secret: issued.secret, keyId: issued.key.keyId });
			}
			onDrawerClose();
		} catch {
			// Mutations surface the API message via toast; keep the drawer open so
			// the operator can correct the campaign or limits.
		}
	};

	return (
		<CustomDrawer
			open={isDrawerOpen}
			onClose={onDrawerClose}
			title={isEdit ? "Edit intake key" : "New intake key"}
			onSubmit={handleSubmit(onSubmitForm)}
			isSubmitButtonDisabled={
				isSubmitting ||
				createMutation.isPending ||
				updateMutation.isPending ||
				// Without a campaign list there is nothing valid to submit, so block
				// the button rather than let it 422 on an empty campaignId.
				(!isEdit && (campaignsFailed || (!campaignsLoading && campaignOptions.length === 0)))
			}
			submitButtonText={isEdit ? "Save changes" : "Create key"}
			width="520px"
		>
			<FormProvider {...methods}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<Controller
							control={control}
							name="name"
							rules={{ required: "A name is required" }}
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth required>
									<InputLabel>Name</InputLabel>
									<OutlinedInput {...field} placeholder="Homepage call-me form" />
									{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

					{!isEdit && campaignsFailed ? (
						<Grid size={12}>
							<Alert severity="error">Could not load your campaigns. Reopen this panel to retry.</Alert>
						</Grid>
					) : null}

					{!isEdit && !campaignsFailed && !campaignsLoading && campaignOptions.length === 0 ? (
						<Grid size={12}>
							<Alert severity="warning">
								You have no campaigns yet. Create a campaign with an agent, a static contact group and a phone number
								first.
							</Alert>
						</Grid>
					) : null}

					<Grid size={12}>
						<Controller
							control={control}
							name="campaignId"
							rules={{ required: "Choose the campaign that will make the call" }}
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth required>
									<InputLabel>Campaign</InputLabel>
									<Select {...field} label="Campaign" disabled={isEdit}>
										{campaignOptions.map((campaign: any) => (
											<MenuItem key={campaign.id} value={campaign.id}>
												{campaign.title}
											</MenuItem>
										))}
									</Select>
									{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

					{campaignId && readiness && !readiness.ready ? (
						<Grid size={12}>
							<Alert severity="warning">
								<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
									This campaign cannot take callback requests yet
								</Typography>
								{readiness.problems.map((problem) => (
									<Typography key={problem} variant="body2">
										- {problem}
									</Typography>
								))}
							</Alert>
						</Grid>
					) : null}

					<Grid size={12}>
						<Controller
							control={control}
							name="allowedCountries"
							render={({ field }) => (
								<Autocomplete
									multiple
									freeSolo
									options={[]}
									open={false}
                                    forcePopupIcon={false}
									value={field.value}
									onChange={(_, value) => field.onChange(value)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip variant="outlined" size="small" color="default" label={option} {...getTagProps({ index })} key={option} />
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Allowed destination countries"
											placeholder="GB"
											helperText="ISO codes, e.g. GB. Leave empty to allow any country - not recommended, this is the main toll-fraud control."
										/>
									)}
								/>
							)}
						/>
					</Grid>

					<Grid size={12}>
						<Controller
							control={control}
							name="allowedIps"
							render={({ field }) => (
								<Autocomplete
									multiple
									freeSolo
									options={[]}
									open={false}
									forcePopupIcon={false}
									value={field.value}
									onChange={(_, value) => field.onChange(value)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip variant="outlined" size="small" color="default" label={option} {...getTagProps({ index })} key={option} />
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Allowed source IPs"
											placeholder="203.0.113.7/32"
											helperText="Your website server's egress IPs or CIDRs. Leave empty to allow any source."
										/>
									)}
								/>
							)}
						/>
					</Grid>

					<Grid size={12}>
						<Controller
							control={control}
							name="fieldMap"
							render={({ field }) => (
								<Autocomplete
									multiple
									freeSolo
									options={[]}
									open={false}
									forcePopupIcon={false}
									value={field.value}
									onChange={(_, value) => field.onChange(value)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												variant="outlined"
												size="small"
												color="default"
												label={option}
												{...getTagProps({ index })}
												key={option}
											/>
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Contact field mapping"
											placeholder="company"
											helperText='Which submitted fields become contact attributes. A field the website sends is ignored unless it is listed here. Use "help_with:notes" to store it under a different attribute name.'
										/>
									)}
								/>
							)}
						/>
						<Typography variant="caption" color="text.secondary">
							This list replaces the whole map, so keep name and email unless you mean to drop them. Unlisted fields
							still reach the agent as lead context - they just do not become filterable contact attributes.
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, md: 6 }}>
						<Controller
							control={control}
							name="defaultCountry"
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth>
									<InputLabel>Default country</InputLabel>
									<OutlinedInput {...field} placeholder="GB" />
									{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 6 }}>
						<Controller
							control={control}
							name="rateLimitPerHour"
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth>
									<InputLabel>Requests per hour</InputLabel>
									<OutlinedInput {...field} type="number" placeholder="60" />
									{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid size={12}>
						<Controller
							control={control}
							name="cooldownSeconds"
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth>
									<InputLabel>Per-number cooldown (seconds)</InputLabel>
									<OutlinedInput {...field} type="number" placeholder="900" />
									{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
								</FormControl>
							)}
						/>
						<Typography variant="caption" color="text.secondary">
							A repeat request for the same number inside this window is treated as a duplicate instead of queueing
							another call.
						</Typography>
					</Grid>

					{isEdit ? (
						<Grid size={12}>
							<Controller
								control={control}
								name="enabled"
								render={({ field }) => (
									<FormControlLabel
										control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
										label="Enabled"
									/>
								)}
							/>
						</Grid>
					) : null}
				</Grid>
			</FormProvider>
		</CustomDrawer>
	);
}
